import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { EventCountdown } from './EventCountdown';
import { InlineEditText } from '../common/InlineEditText';

export interface EventHeaderProps {
  /** Where the breadcrumb's first crumb leads ("Events", "My events"). */
  backTo: { label: string; to: string };
  title: string;
  /** Makes the name editable in place. */
  onRename?: (name: string) => Promise<unknown>;
  startAt?: Date | string | null;
  /** The logo, usually `<EventLogo>`. */
  logo?: ReactNode;
  /** Pills under the title: status, date, venue, type, balance. */
  meta?: ReactNode;
  /** Buttons on the right (Edit, More…). */
  actions?: ReactNode;
  /** The tab row, drawn along the header's bottom edge. */
  tabs?: ReactNode;
  /** Pull the header out to the edges of the page padding and pin it. */
  sticky?: boolean;
  compact?: boolean;
}

/**
 * The header shared by the producer's event workspace and the client portal's
 * event page: breadcrumb, logo, name with a countdown, a row of pills, actions
 * on the right and the tabs underneath. It stays pinned while the tab below
 * scrolls, so the event is always in view and no page needs a "Back" button.
 */
export function EventHeader({
  backTo,
  title,
  onRename,
  startAt,
  logo,
  meta,
  actions,
  tabs,
  sticky = true,
  compact = false,
}: EventHeaderProps) {
  // The page's scroll container is padded (p-6, p-3 on a phone). A sticky
  // box sticks inside that padding, so `top` is the negative padding — with
  // `top-0` it slid down by the padding once pinned and covered the content.
  const pin = sticky
    ? compact
      ? 'sticky -top-3 z-20 -mx-3 -mt-3 px-3'
      : 'sticky -top-6 z-20 -mx-6 -mt-6 px-6'
    : 'rounded-xl border px-4 md:px-6';
  return (
    <header
      className={`${pin} bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 border-gray-200 border-b pt-4 mb-6`}
    >
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-gray-500">
        <Link to={backTo.to} className="hover:text-primary-700">
          {backTo.label}
        </Link>
        <ChevronRight size={12} aria-hidden />
        <span className="truncate text-gray-700">{title}</span>
      </nav>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          {logo}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1
                className={`${compact ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 tracking-tight`}
              >
                {onRename ? (
                  <InlineEditText value={title} onSave={onRename} label="Event name" />
                ) : (
                  title
                )}
              </h1>
              <EventCountdown startAt={startAt} />
            </div>
            {meta && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                {meta}
              </div>
            )}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {tabs && <div className="mt-3">{tabs}</div>}
      {!tabs && <div className="pb-4" />}
    </header>
  );
}

/** A quiet outlined pill for the header's meta row (date, venue…). */
export function MetaChip({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 text-xs text-gray-600 bg-white">
      {icon}
      {children}
    </span>
  );
}
